import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CommentsModal from "./components/CommentsModal";
import ImageGallery from "react-image-gallery";
import {
	MdArrowBack,
	MdLocationOn,
	MdPlace,
	MdPerson,
	MdPhone,
	MdManageAccounts,
	MdBadge,
	MdMyLocation,
	MdSchool,
	MdSupportAgent,
	MdCheckCircle,
	MdWarning,
	MdInfoOutline,
	MdDirections,
	MdEventAvailable,
	MdRateReview,
	MdBed,
	MdErrorOutline,
} from "react-icons/md";
import Iframe from "./components/utils/Iframe";
import { ToastContainer } from "react-toastify";
import AxiosInstance from "./components/utils/AxiosBase";
import oidcAuthService from "../services/OidcAuthService";
import { Navigation, Pagination, Scrollbar, A11y } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import Residence from "./components/RelatedResidences";

const imagesUrl = `${process.env.REACT_APP_API_URL}/api/images`;
const defaultImages = [{ original: `${imagesUrl}/90ef/cover-image-1646409988773.jpeg` }];

// one row in the contacts list; phone-like values become tap-to-call links
function InfoRow({ icon, label, value, isPhone = false }) {
	const display = value ?? "N/A";
	return (
		<li className="rd-info-row">
			<span className="rd-info-icon">{icon}</span>
			<span className="rd-info-body">
				<span className="rd-info-label">{label}</span>
				{isPhone && value ? (
					<a className="rd-info-value" href={`tel:${String(value).replace(/\s/g, "")}`}>
						{display}
					</a>
				) : (
					<span className="rd-info-value">{display}</span>
				)}
			</span>
		</li>
	);
}

export default function Hosteldetails(props) {
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [residence, setResidence] = useState();
	const [settings, setSettings] = useState();
	const [user, setUser] = useState(null);
	const [facilities, setFacilities] = useState([]);
	const [related, setRelated] = useState([]);
	const [gimages, setGimages] = useState([]);
	let { slug } = useParams();

	useEffect(() => {
		const fetchUser = async () => {
			const currentUser = await oidcAuthService.getUser();
			setUser(currentUser?.profile);
		};
		fetchUser();
	}, []);

	useEffect(() => {
		const fetchSettings = async () => {
			try {
				const res = await AxiosInstance({
					method: "get",
					url: `/api/v1/settings`,
				});

				setSettings(res.data.data[0]);
			} catch (err) {
				// toast.error(err.response.message, { position: "top-center" });
			}
		};

		fetchSettings();
	}, []);

	useEffect(() => {
		const controller = new AbortController();
		const fetchData = async () => {
			setIsLoading(true);
			setHasError(false);
			try {
				const res = await AxiosInstance({
					method: "get",
					url: `/api/v1/residences/${slug}`,
					signal: controller.signal,
				});
				const data = res.data?.data;
				if (!data) throw new Error("Residence not found");

				// cover image first, then the rest of the gallery
				const files = [data.coverImage, ...(data.images || [])].filter(Boolean);
				setGimages(
					files.map((file) => ({
						original: `${imagesUrl}/${slug}/${file}`,
						thumbnail: `${imagesUrl}/${slug}/${file}`,
					}))
				);
				setRelated(res.data.relatedArray || []);
				setResidence(data);
				setFacilities(res.data.facilities || []);
			} catch (err) {
				if (controller.signal.aborted) return;
				console.log(err);
				setHasError(true);
			} finally {
				if (!controller.signal.aborted) setIsLoading(false);
			}
		};

		fetchData();
		window.scrollTo(0, 0);
		return () => controller.abort();
	}, [slug]);

	if (isLoading) {
		return (
			<div className="rd-page">
				<div id="loading" />
				<div className="load-separator-details" />
			</div>
		);
	}

	if (hasError || !residence) {
		return (
			<div className="rd-page container">
				<div className="rd-card rd-state">
					<MdErrorOutline className="rd-state-icon" />
					<h2>We couldn't load this residence</h2>
					<p>It may have been removed, or there was a network problem.</p>
					<button className="rd-btn rd-btn--primary" onClick={() => navigate(-1)}>
						<MdArrowBack /> Go Back
					</button>
				</div>
			</div>
		);
	}

	const zone = residence?.location?.zone;
	const rClass = residence?.rClass;
	const coords = residence?.gpsAddress?.coordinates;
	const hasMap = Array.isArray(coords) && coords[0] !== 0 && coords[1] != null;
	const directionsUrl = hasMap
		? `https://www.google.com/maps/dir/?api=1&destination=${coords[1]},${coords[0]}`
		: null;
	const showRegistrationWarning =
		settings?.registrationDisplay && !residence.registered;
	const relatedItems = (related || []).slice(0, 8);

	const priceRows = [
		{ label: "Two in One Room", value: rClass?.category?.twoInOne },
		{ label: "Three in One Room", value: rClass?.category?.threeInOne },
		{ label: "Four in One Room", value: rClass?.category?.fourInOne },
	];
	const actualPrices = [
		{ label: "Rooms", value: residence?.bedSpaceStatus },
		{ label: "2-in-1", value: residence?.actualPrices?.twoInOne },
		{ label: "3-in-1", value: residence?.actualPrices?.threeInOne },
		{ label: "4-in-1", value: residence?.actualPrices?.fourInOne },
	];

	return (
		<div className="rd-page">
			<ToastContainer style={{ marginTop: 90 }} />

			{/* header */}
			<header className="container rd-hero">
				<button className="rd-back" onClick={() => navigate(-1)}>
					<MdArrowBack /> Back
				</button>
				<div className="rd-hero-row">
					<div className="rd-hero-text">
						<h1 className="rd-title">{residence?.name ?? "N/A"}</h1>
						<div className="rd-chips">
							<span className="rd-chip">
								<MdLocationOn /> {residence?.location?.name ?? "N/A"}
							</span>
							<span className="rd-chip">
								<MdPlace /> {zone?.name ?? "N/A"}
							</span>
							{settings?.registrationDisplay && (
								<span
									className={`rd-chip ${
										residence.registered ? "rd-chip--ok" : "rd-chip--warn"
									}`}
								>
									{residence.registered ? <MdCheckCircle /> : <MdWarning />}
									{residence.registered ? "Registered" : "Not Registered"}
								</span>
							)}
						</div>
					</div>
					<div className="rd-class" title="Residence classification">
						<span className="rd-class-name">{rClass?.name ?? "N/A"}</span>
						<span className="rd-class-caption">Class</span>
					</div>
				</div>
			</header>

			<div className="container rd-layout">
				{/* main column */}
				<main className="rd-main">
					<section className="rd-card rd-gallery">
						<ImageGallery
							items={gimages.length ? gimages : defaultImages}
							showThumbnails={gimages.length > 1}
							showPlayButton={gimages.length > 1}
							lazyLoad
						/>
					</section>

					<section className="rd-card">
						<h2 className="rd-section-title">Description</h2>
						<p className="rd-description">{residence?.description ?? "N/A"}</p>
					</section>

					<section className="rd-card">
						<h2 className="rd-section-title">Facilities</h2>
						{facilities.length === 0 ? (
							<p className="rd-muted">Not Available</p>
						) : (
							<ul className="rd-facilities">
								{facilities.map((item) => (
									<li key={item._id} className="rd-facility">
										<MdCheckCircle className="rd-facility-icon" />
										<span>{item?.facility?.name}</span>
									</li>
								))}
							</ul>
						)}
					</section>

					{rClass?.name && (
						<section className="rd-card">
							<h2 className="rd-section-title">Potential Price Range</h2>
							<ul className="rd-prices">
								{priceRows.map((row) => (
									<li key={row.label} className="rd-price">
										<span className="rd-price-label">
											<MdBed /> {row.label}
										</span>
										<span className="rd-price-value">
											GH&#8373; {row.value ?? "N/A"}
										</span>
									</li>
								))}
							</ul>
							<p className="rd-alert rd-alert--danger">
								<MdWarning /> One in a Room Not Recommended !!
							</p>
						</section>
					)}

					<section className="rd-card">
						<h2 className="rd-section-title">Location on Map</h2>
						{hasMap ? (
							<>
								<p className="rd-alert rd-alert--info">
									<MdInfoOutline />
									<span>
										Click <b>Directions</b> / <b>View Larger Map</b> on the map, or
										use the button below to visit the residence
									</span>
								</p>
								<div className="rd-map">
									<Iframe name={residence?.name} lat={coords[1]} lng={coords[0]} />
								</div>
								<a
									className="rd-btn rd-btn--outline rd-btn--block"
									href={directionsUrl}
									target="_blank"
									rel="noopener noreferrer"
								>
									<MdDirections /> Get Directions
								</a>
							</>
						) : (
							<p className="rd-muted">Map View Not Available yet</p>
						)}
					</section>
				</main>

				{/* sidebar */}
				<aside className="rd-side">
					<section className="rd-card rd-actions">
						{settings?.showAmount && (
							<div className="rd-stats">
								{actualPrices.map((item) => (
									<div key={item.label} className="rd-stat">
										<span className="rd-stat-label">{item.label}</span>
										<span className="rd-stat-value">{item.value ?? "N/A"}</span>
									</div>
								))}
							</div>
						)}
						{residence?.bookingLink && (
							<a
								className="rd-btn rd-btn--primary rd-btn--block"
								href={`${residence.bookingLink}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<MdEventAvailable /> Book Now
							</a>
						)}
						{user ? (
							<CommentsModal
								id={residence?._id}
								user={user}
								buttonClassName="rd-btn rd-btn--outline rd-btn--block"
							/>
						) : (
							<button
								className="rd-btn rd-btn--outline rd-btn--block"
								onClick={() => oidcAuthService.login()}
							>
								<MdRateReview /> Leave a Comment
							</button>
						)}
					</section>

					<section className="rd-card">
						<h2 className="rd-section-title">Contacts &amp; Management</h2>
						<ul className="rd-info">
							<InfoRow icon={<MdPerson />} label="Owner's Name" value={residence.ownersName} />
							{showRegistrationWarning ? (
								<li className="rd-alert rd-alert--danger">
									<MdWarning /> <b>Residence Not Registered</b>
								</li>
							) : (
								<InfoRow
									icon={<MdPhone />}
									label="Owner's Contact"
									value={residence.ownersContact}
									isPhone
								/>
							)}
							<InfoRow
								icon={<MdManageAccounts />}
								label="Manager's Name"
								value={residence.managersName}
							/>
							<InfoRow
								icon={<MdBadge />}
								label="Porter's / Assistant Manager's Name"
								value={residence.portersName}
							/>
							<InfoRow
								icon={<MdMyLocation />}
								label="Digital Address"
								value={residence.digitalAddress}
							/>
						</ul>
					</section>

					<section className="rd-card">
						<h2 className="rd-section-title">Student Support</h2>
						<ul className="rd-info">
							<InfoRow icon={<MdSchool />} label="Senior Tutor" value={zone?.tutor?.name} />
							<InfoRow
								icon={<MdSupportAgent />}
								label="Counsellor's Name"
								value={zone?.counsellor?.name}
							/>
							<InfoRow
								icon={<MdPhone />}
								label="Counsellor's Contact"
								value={zone?.counsellor?.contact}
								isPhone
							/>
						</ul>
					</section>
				</aside>
			</div>

			{relatedItems.length > 0 && (
				<section className="container rd-related">
					<h2 className="rd-related-title">Related Residences</h2>
					<Swiper
						modules={[Navigation, Pagination, Scrollbar, A11y]}
						spaceBetween={20}
						slidesPerView={1}
						loop={relatedItems.length > 4}
						navigation
						pagination={{ clickable: true }}
						breakpoints={{
							576: { slidesPerView: 2 },
							992: { slidesPerView: 3 },
							1200: { slidesPerView: 4 },
						}}
					>
						{relatedItems.map((item) => (
							<SwiperSlide key={item?._id} className="rd-slide">
								<Residence residence={item} />
							</SwiperSlide>
						))}
					</Swiper>
				</section>
			)}
		</div>
	);
}
